import ActionsDropdown from '@shared/components/dropdowns/actions-dropdown';
import ActionsDropdownItem from '@shared/components/dropdowns/actions-dropdown/actions-dropdown-item';
import { Icon, IconNames } from '@shared/components/icons';
import { html } from '@shared/utils/rendering/html';
import { CacheKeyNames } from '@core/constants/utility/cache-key-names';
import { EvidenceStatusMap } from '@core/constants/chips/evidence-status.constants';
import { useAuth } from '@core/utils/context/authentication-context';
import { useCache } from '@core/utils/context/cache-context';
import { useFeatureChecks } from '@core/utils/features/feature-context';
import { usePatch } from '@core/utils/hooks/use-patch';
import { useQueryClient } from '@tanstack/react-query';
import AttestationHistory from './attestation-history';
import ReopenEvidenceModal from './reopen-evidence';
import { useState } from 'preact/hooks';

const SubcomponentActionsComponent = ({ data }) => {
    const [isHistoryVisible, setIsHistoryVisible] = useState(false);
    const [isReopenVisible, setIsReopenVisible] = useState(false);
    const queryClient = useQueryClient();
    const getCache = useCache().getKey;
    const { roles } = useAuth();
    const { useFeature } = useFeatureChecks();
    const canReopenAttestation = useFeature('evidence.canReopenAttestation', false);
    const { patch: reopenEvidence, isSubmitting } = usePatch('/evidence/reopen', {
        successMessage: 'Attestation reopened successfully.',
    });

    const canReopenRole =
        Array.isArray(roles) && (roles.includes('Admin') || roles.includes('Champion'));
    const canReopen =
        canReopenAttestation &&
        canReopenRole &&
        !!data?.attestationRecordId &&
        data?.statusId === EvidenceStatusMap.approved;

    const onReopenConfirm = async (comment) => {
        await reopenEvidence({
            attestationRecordId: data.attestationRecordId,
            comment,
        });

        setIsReopenVisible(false);

        const assessmentKey = getCache(CacheKeyNames.legalEntityAssessmentData)?.cacheKey;
        if (assessmentKey) {
            queryClient.invalidateQueries({ queryKey: assessmentKey });
        }

        const historyKey = getCache(CacheKeyNames.attestationHistoryTable)?.cacheKey;
        if (historyKey) {
            queryClient.invalidateQueries({ queryKey: historyKey });
        }
    };

    return html`
    <${AttestationHistory}
         recordId=${data.attestationRecordId}
         isVisible=${isHistoryVisible}
         closeModal=${() => setIsHistoryVisible(false)}
         title=${data.subcomponentName}
    />
    ${
        canReopen
            ? html`<${ReopenEvidenceModal}
                  isVisible=${isReopenVisible}
                  closeModal=${() => setIsReopenVisible(false)}
                  onConfirm=${onReopenConfirm}
                  isSubmitting=${isSubmitting}
              />`
            : null
    }
    <${ActionsDropdown}
        id="actions-dropdown-${data.id}"
        icon=${IconNames.MoreVert}
        css=""
    >
        <${ActionsDropdownItem}
            disabled=${!data?.attestationRecordId}
            onClickEvent=${() => setIsHistoryVisible(true)}
            params=${{}}
            label=${{
                text: 'View History',
                icon: html`<${Icon} name=${IconNames.History} />`,
            }}
        />
        ${
            canReopen
                ? html`<${ActionsDropdownItem}
                      disabled=${isSubmitting}
                      onClickEvent=${() => setIsReopenVisible(true)}
                      params=${{}}
                      label=${{
                          text: 'Reopen Attestation',
                          icon: html`<${Icon} name=${IconNames.ReopenWindow} />`,
                      }}
                  />`
                : null
        }
    </${ActionsDropdown}>`;
};

const subcomponentActions = (data) => {
    return html`<${SubcomponentActionsComponent} data=${data} />`;
};

export default subcomponentActions;
